import { PdfRenderer } from 'elements/PdfRenderer'
import { HardwareComponentType } from 'elements/hardwareSelectorV2/types'
import { StandData } from 'opensolar-pageeditor/src/renderer/expoStand'
import { sanitizeHtml } from 'opensolar-pageeditor/src/renderer/utils'
import { styled } from 'opensolar-ui'
import { FC, useMemo } from 'react'
import CompanyContent from './CompanyContent'
import ExpoBanner from './ExpoBanner'
import TechnicalContent from './TechnicalContent'

export type TabType = 'Overview' | 'Technical' | 'Company'
interface PropTypes {
  tabSelected: TabType
  component: HardwareComponentType
  parsedComponentData?: Record<string, unknown>
  standData?: StandData
  isStandDataLoading?: boolean
}
const TabWrapper = styled('div', { name: 'TabContentWrapper' })<{ isPdf?: boolean }>(({ theme, isPdf }) => ({
  marginBottom: 20,
  display: 'flex',
  flexDirection: 'column',
  width: isPdf ? '100%' : '90%',
}))

const OverviewContainer = styled('div', { name: 'OverviewContainer' })({
  '& *': {
    maxWidth: '100%',
  },
})
const ContentWrapper = styled('div', { name: 'ContentWrapper' })<{ isPdf?: boolean }>(({ isPdf }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  ...(isPdf && { overflow: 'hidden' }),
}))

const PdfContainer = styled('div', { name: 'PdfContainer' })(({ theme }) => ({
  width: '100%',
  height: '60vh',
  [theme.breakpoints.down('sm')]: {
    height: '70vh',
    padding: theme.spacing(0.5),
  },
}))

const TabContent: FC<PropTypes> = ({ tabSelected, component, parsedComponentData, standData, isStandDataLoading }) => {
  const isPdf = tabSelected === 'Overview' && component.component_content?.product_details_is_pdf === true
  const pdfUrl = component.component_content?.product_details_pdf_url

  const content = useMemo(() => {
    switch (tabSelected) {
      case 'Overview':
        if (isPdf && pdfUrl != null) {
          return (
            <PdfContainer>
              <PdfRenderer pdfUrl={pdfUrl} scrollable={true} allowDownload={true} />
            </PdfContainer>
          )
        }

        const htmlDetails = component.product_details || component.component_content?.product_details
        return htmlDetails && <OverviewContainer dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlDetails) }} />
      case 'Technical':
        return <TechnicalContent component={component} parsedComponentData={parsedComponentData} />
      case 'Company':
        return !isStandDataLoading && standData?.expoStand ? <CompanyContent {...standData?.expoStand} /> : null
      default:
        return null
    }
  }, [tabSelected, component.component_content, parsedComponentData])

  return (
    <TabWrapper isPdf={isPdf && pdfUrl != null}>
      <ContentWrapper isPdf={isPdf && pdfUrl != null}>{content}</ContentWrapper>
      {standData && (
        <div style={{ width: '100%' }}>
          <ExpoBanner {...standData} />
        </div>
      )}
    </TabWrapper>
  )
}

export default TabContent
